import { useCallback, useEffect, useState } from 'react';

const DISMISS_KEY = 'jixxeo_install_dismissed_until';

function detectPlatform() {
  if (typeof navigator === 'undefined') return { ios: false, android: false, mac: false, windows: false, safari: false, chrome: false, edge: false, firefox: false, samsung: false, isMobile: false };
  const ua = navigator.userAgent || '';
  const ios = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const android = /Android/.test(ua);
  const mac = !ios && /Macintosh/.test(ua);
  const windows = /Windows/.test(ua);
  const chrome = /Chrome/.test(ua) && !/Edg/.test(ua) && !/OPR/.test(ua) && !/SamsungBrowser/.test(ua);
  const edge = /Edg\//.test(ua);
  const firefox = /Firefox/.test(ua);
  const samsung = /SamsungBrowser/.test(ua);
  const safari = /Safari/.test(ua) && !chrome && !edge && !firefox && !samsung;
  return { ios, android, mac, windows, safari, chrome, edge, firefox, samsung, isMobile: ios || android };
}

function detectStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.matchMedia?.('(display-mode: minimal-ui)').matches ||
    window.matchMedia?.('(display-mode: window-controls-overlay)').matches ||
    window.navigator?.standalone === true
  );
}

/**
 * usePwaInstall — one source of truth for install status and prompt.
 *   deferredPrompt: BeforeInstallPromptEvent when Android/Chrome/Edge/Samsung fire it
 *   installed:      running as installed PWA right now
 *   installable:    the browser gave us a deferred prompt
 *   platform:       user-agent detection
 *   canPromptIOS:   iOS Safari (no beforeinstallprompt event; manual "Share → Add to Home Screen")
 *   promptInstall(): calls the deferred prompt if available
 *   dismiss(): hides the auto-banner for 7 days
 *   dismissedUntil: timestamp when banner is dismissed until
 */
export function usePwaInstall() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(() => detectStandalone());
  const [platform] = useState(() => detectPlatform());
  const [dismissedUntil, setDismissedUntil] = useState(() => {
    try { return parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10) || 0; } catch { return 0; }
  });

  useEffect(() => {
    const onBip = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setDeferred(null); setInstalled(true); };
    const media = window.matchMedia?.('(display-mode: standalone)');
    const onMediaChange = () => setInstalled(detectStandalone());
    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', onInstalled);
    media?.addEventListener?.('change', onMediaChange);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onInstalled);
      media?.removeEventListener?.('change', onMediaChange);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return { outcome: 'unavailable' };
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      return choice; // { outcome: 'accepted' | 'dismissed', platform }
    } catch {
      return { outcome: 'error' };
    }
  }, [deferred]);

  const dismiss = useCallback((days = 7) => {
    const until = Date.now() + days * 24 * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_KEY, String(until));
    setDismissedUntil(until);
  }, []);

  const clearDismiss = useCallback(() => {
    localStorage.removeItem(DISMISS_KEY);
    setDismissedUntil(0);
  }, []);

  return {
    installed,
    platform,
    deferredAvailable: !!deferred,
    installable: !!deferred,
    canPromptIOS: platform.ios && platform.safari && !installed,
    promptInstall,
    dismiss,
    clearDismiss,
    dismissedUntil,
    isDismissed: Date.now() < dismissedUntil,
  };
}
